import React, { useState } from "react";
import { Pie } from "react-chartjs-2";
import { Link } from "react-router-dom";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Helmet } from "react-helmet";
import FAQSchema from "../../FAQSchema";

ChartJS.register(ArcElement, Tooltip, Legend);

const CanaraBankCalculator = ({ isSip, setIsSip }) => {
  const [monthlyInvestment, setMonthlyInvestment] = useState(25000);
  const [expectedReturnRate, setExpectedReturnRate] = useState(12);
  const [timePeriod, setTimePeriod] = useState(10);

  const calculateSIP = () => {
    const monthlyRate = expectedReturnRate / 100 / 12;
    const months = timePeriod * 12;
    const investedAmount = monthlyInvestment * months;
    const futureValue =
      monthlyInvestment *
      ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate) *
      (1 + monthlyRate);
    const estimatedReturns = futureValue - investedAmount;

    return {
      investedAmount,
      estimatedReturns,
      totalValue: futureValue,
    };
  };

  const { investedAmount, estimatedReturns, totalValue } = calculateSIP();

  const data = {
    labels: ["Invested Amount", "Estimated Returns"],
    datasets: [
      {
        label: "SIP Returns Breakdown",
        data: [investedAmount, estimatedReturns],
        backgroundColor: ["#36A2EB", "#FF6384"],
      },
    ],
  };
  function Icon({ id, open }) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
        className={`${
          id === open ? "rotate-180" : ""
        } h-5 w-5 transition-transform`}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M19.5 8.25l-7.5 7.5-7.5-7.5"
        />
      </svg>
    );
  }

  const [activeIndex, setActiveIndex] = useState(0);

  const faqs = [
    {
        question: "What is the Kotak Mutual Fund SIP Calculator?",
        answer:
          "It is a digital tool that estimates the future maturity value of your SIP investments in Kotak mutual funds.",
      },
      {
        question: "Is it free to use?",
        answer:
          "Yes, it’s completely free and accessible to anyone planning SIP investments.",
      },
      {
        question: "What’s the minimum SIP amount I can start with?",
        answer:
          "You can start investing in Kotak SIPs with as little as ₹500 per month.",
      },
      {
        question: "Can the calculator help with goal-based planning?",
        answer:
          "Yes, simply enter your desired corpus and the tenure to get the recommended SIP amount.",
      },
      {
        question: "Are the results accurate?",
        answer:
          "Results are projections based on your inputs. Actual results may vary based on market conditions.",
      },
      {
        question: "Can I adjust the return rate?",
        answer:
          "Yes, the calculator lets you input custom expected return rates (e.g., 8%, 10%, 12%).",
      },
      {
        question: "Are SIP returns taxed?",
        answer:
          "Yes. Equity gains above ₹1 lakh are taxed at 10% (LTCG), and debt fund gains are taxed as per your income slab (post indexation if long-term).",
      },
      {
        question: "Can the calculator be used for non-Kotak funds?",
        answer:
          "Mathematically, yes. But for fund-specific insights, use the official Kotak or TheFinmaster calculator.",
      },
      {
        question: "How often should I review my SIP?",
        answer:
          "Ideally, every 6–12 months or after major life events or market shifts.",
      },
      {
        question: "Can I increase my SIP amount mid-way?",
        answer:
          "Yes, many Kotak funds allow SIP top-ups (also known as step-up SIPs).",
      },
      {
        question: "Does the calculator account for inflation?",
        answer:
          "Not directly, but you can adjust your expected return rate or goal amount accordingly.",
      },
      {
        question: "What’s better—monthly SIP or quarterly SIP?",
        answer:
          "Monthly SIPs offer smoother cost averaging and better market capture.",
      },
      {
        question: "Is there a mobile version of the calculator?",
        answer:
          "Yes, both Kotak and TheFinmaster offer responsive mobile interfaces.",
      },
      {
        question: "Can I save or share my SIP plan?",
        answer:
          "Yes, use the download or share options on the page to keep a record or send it to your advisor.",
      }
    ]

  const toggleAccordion = (index) => {
    if (activeIndex === index) {
      // If the clicked question is already expanded, collapse it
      setActiveIndex(null);
    } else {
      // If the clicked question is not expanded, expand it
      setActiveIndex(index);
    }
  };
  return (
    <>
      <Helmet>
        <title>
        Canara Bank SIP Calculator – Mutual Fund Growth Planner
        </title>
        <meta
          name="description"
          content="Estimate your Canara Bank SIP returns easily. This tool helps you strategize your mutual fund goals and track expected growth."
        />     
        <link
          rel="canonical"
          href="https://thefinmaster.com/financial-tools-calculators/canara-bank-sip-calculator"
        />
        <meta
          name="robots"
          content="max-snippet:-1, max-image-preview:standard"
        />
        <meta name="robots" content="max-video-preview:-1" />
      </Helmet>
      <FAQSchema faqs={faqs} />
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800 ">
              Home
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <Link
              to="/financial-tools-calculators"
              className="text-blue-600 hover:text-blue-800"
            >
              Financial Tools Calculators
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500 "> Canara Bank SIP Calculator</span>
          </li>
        </ol>
      </nav>
      <div className="py-6">
        <div className="container mx-auto p-4 shadow-xl rounded-md ">
          <h1 className="text-2xl font-bold mb-4">
          Canara Bank SIP Returns Calculator
          </h1>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <div>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold">
                  Monthly Investment
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="0"
                    max="100000"
                    value={monthlyInvestment}
                    onChange={(e) =>
                      setMonthlyInvestment(Number(e.target.value))
                    }
                    className="w-1/2 p-2 border rounded mr-2"
                  />
                  <span className="text-gray-700">
                    ₹ {monthlyInvestment.toLocaleString()}
                  </span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100000"
                  value={monthlyInvestment}
                  onChange={(e) => setMonthlyInvestment(Number(e.target.value))}
                  className="w-full mt-2"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold">
                  Expected Return Rate (P.A.)
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={expectedReturnRate}
                    onChange={(e) =>
                      setExpectedReturnRate(Number(e.target.value))
                    }
                    className="w-1/2 p-2 border rounded mr-2"
                  />
                  <span className="text-gray-700">{expectedReturnRate} %</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="0.1"
                  value={expectedReturnRate}
                  onChange={(e) =>
                    setExpectedReturnRate(Number(e.target.value))
                  }
                  className="w-full mt-2"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold">
                  Time Period (Years)
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="1"
                    max="50"
                    value={timePeriod}
                    onChange={(e) => setTimePeriod(Number(e.target.value))}
                    className="w-1/2 p-2 border rounded mr-2"
                  />
                  <span className="text-gray-700">{timePeriod} Yr</span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="50"
                  value={timePeriod}
                  onChange={(e) => setTimePeriod(Number(e.target.value))}
                  className="w-full mt-2"
                />
              </div>
              <button
                onClick={calculateSIP}
                className="bg-blue-500 text-white px-4 py-2 rounded"
              >
                Calculate
              </button>
            </div>
            <div>
              <div className="mb-4">
                <h2 className="text-xl font-bold">Results</h2>
                <p>Invested Amount: ₹ {investedAmount.toLocaleString()}</p>
                <p>Estimated Returns: ₹ {estimatedReturns.toLocaleString()}</p>
                <p>Total Value: ₹ {totalValue.toLocaleString()}</p>
              </div>
              <div>
                <h2 className="text-xl font-bold">Breakdown</h2>
                <div className="flex justify-center">
                  <Pie
                    data={data}
                    options={{ maintainAspectRatio: false }}
                    width={300}
                    height={300}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap lg:px-6 md:px-6 px-0 py-6">
        <div className="w-full md:w-2/3 p-4 mx-auto">
        <div className="container mx-auto py-4">
          <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
            What is the Canara Bank SIP Calculator?
          </h1>
          <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
            The Canara Bank SIP Calculator is a powerful online tool designed to
            help you estimate your potential returns from Systematic Investment
            Plans (SIPs) in mutual funds. SIPs are a disciplined approach to
            investing, where investors contribute a fixed amount regularly to
            mutual funds. The Canara Bank SIP Calculator allows users to input
            their monthly investment amount, expected rate of return, and the
            investment tenure, to calculate the projected value of their
            investment at the end of the specified period. This calculator helps
            investors plan their investments more effectively and align them
            with their financial goals.
          </p>
</div>
          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              What is SIP and Why is it Popular?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              A Systematic Investment Plan (SIP) is a method of investing a
              fixed sum of money regularly into mutual funds. The money invested
              through SIPs is used to purchase units of mutual funds, which are
              pooled with other investors' funds to create a diversified
              portfolio.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The popularity of SIPs stems from their ability to:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>
                <strong>Promote disciplined investing:</strong> Since SIPs are
                regular investments, they help investors stay on track with
                their financial goals.
              </li>
              <li>
                <strong>Dollar-cost averaging:</strong> By investing a fixed
                amount at regular intervals, SIPs help average the cost of units
                over time, reducing the impact of market volatility.
              </li>
              <li>
                <strong>Affordability:</strong> Investors can start SIPs with as
                low as ₹500, making them accessible to a wide range of people.
              </li>
              <li>
                <strong>Compounding growth:</strong> Regular investments in
                mutual funds allow for compound growth, where the returns
                generated on the initial investment are reinvested, leading to
                exponential growth over time.
              </li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How Can the Canara Bank SIP Calculator Help You?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The Canara Bank SIP Calculator helps investors by providing:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>
                <strong>Investment Planning:</strong> By inputting your monthly
                investment, expected rate of return, and duration, the SIP
                calculator helps you gauge the amount you could accumulate by
                the end of your investment tenure.
              </li>
              <li>
                <strong>Return Estimates:</strong> It helps in understanding how
                much your monthly SIP contributions will grow over time with the
                expected returns, making it easier to set realistic financial
                goals.
              </li>
              <li>
                <strong>Comparing Different Scenarios:</strong> The calculator
                allows you to experiment with different SIP amounts, time
                horizons, and expected returns, giving you a range of possible
                outcomes.
              </li>
              <li>
                <strong>Making Informed Decisions:</strong> By understanding
                potential future values, the calculator helps you decide if you
                need to increase your SIP amount or adjust the duration to meet
                your financial goals.
              </li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How Does the Canara Mutual Fund SIP Calculator Work?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The Canara Mutual Fund SIP Calculator works by applying a standard
              formula for SIP returns that factors in:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>
                <strong>Monthly SIP Contribution:</strong> The fixed amount you
                plan to invest every month.
              </li>
              <li>
                <strong>Rate of Return:</strong> The expected annual return from
                the mutual fund, typically provided by historical performance
                data.
              </li>
              <li>
                <strong>Investment Duration:</strong> The time period for which
                you plan to invest, usually in years.
              </li>
              <li>
                <strong>Compounding Frequency:</strong> Typically, the returns
                are compounded quarterly or annually, which impacts the final
                corpus.
              </li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The formula used to calculate SIP returns is based on the compound
              interest formula:
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Formula:</strong>
              <br />M = P × [(1 + i)<sup>n</sup> - 1] / i × (1 + i)
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Where:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>M = Maturity amount</li>
              <li>P = SIP amount per month</li>
              <li>i = Monthly rate of return = (Annual Return ÷ 12)</li>
              <li>n = Number of months (Investment tenure)</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              This formula calculates the accumulated value of your SIP
              contributions after applying the rate of return and considering
              compounding over the specified duration.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How to Use the Canara Bank SIP Calculator Online
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Using the Canara Bank SIP Calculator is simple and intuitive.
              Here’s a step-by-step guide:
            </p>
            <ol className="list-decimal pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>
                Enter Monthly SIP Amount: Begin by entering the fixed amount you
                plan to invest every month in the input field.
              </li>
              <li>
                Select the Expected Rate of Return: Based on historical
                performance or your expectations, choose the annual rate of
                return for your mutual fund investment.
              </li>
              <li>
                Set the Duration: Input the time period for your SIP in months
                or years.
              </li>
              <li>
                Click Calculate: Once you’ve entered the details, hit the
                ‘Calculate’ button. The calculator will display the projected
                value of your investment at the end of the selected duration.
              </li>
              <li>
                Adjust Parameters: You can experiment with different SIP
                amounts, return rates, or investment durations to see how
                different variables impact your final corpus.
              </li>
            </ol>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Advantages of Using TheFinMaster’s SIP Calculator
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The FinMaster SIP Calculator offers several advantages for
              investors looking to plan their investments:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>
                <strong>Accuracy:</strong> The calculator uses advanced
                algorithms to provide accurate estimates of your potential
                returns.
              </li>
              <li>
                <strong>User-Friendly:</strong> With an easy-to-use interface,
                the calculator ensures a seamless experience for both beginners
                and experienced investors.
              </li>
              <li>
                <strong>Customization:</strong> You can adjust the variables
                such as the SIP amount, tenure, and expected return to match
                your specific financial goals.
              </li>
              <li>
                <strong>Instant Results:</strong> Unlike traditional methods of
                calculation, this tool gives you instant, easy-to-understand
                results.
              </li>
              <li>
                <strong>Free of Cost:</strong> The FinMaster SIP calculator is
                available for free, helping investors plan their SIP investments
                without additional costs.
              </li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Which Factors Influence SIP Earnings?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Several factors can influence the final returns on your SIP
              investment:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>
                <strong>Rate of Return:</strong> The annual return you expect
                from the mutual fund plays a crucial role in determining your
                SIP earnings. The higher the return, the more your investment
                will grow over time.
              </li>
              <li>
                <strong>Investment Amount:</strong> The amount you invest each
                month significantly impacts the final corpus. Higher
                contributions generally result in higher returns.
              </li>
              <li>
                <strong>Investment Duration:</strong> The longer the duration of
                the SIP, the more time your investments have to compound and
                grow.
              </li>
              <li>
                <strong>Market Volatility:</strong> While SIPs are designed to
                smooth out market volatility, significant market fluctuations
                can still influence the returns over short periods.
              </li>
              <li>
                <strong>Compounding Frequency:</strong> The frequency with which
                returns are compounded (quarterly or annually) will affect the
                total returns you earn.
              </li>
            </ul>
          </div>
        </div>

        <div className="w-full md:w-1/3 p-4">
          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            <div className="bg-blue-100 px-4 py-2 border-b border-blue-600 ">
              <h2 className="text-lg font-semibold ">Popular Categories</h2>
            </div>
            <div className="p-2 ">
              <ul>
                <Link to="/financial-tools-calculators/swp-calculator">
                  <li className="py-2  hover:bg-slate-100 border-b border-gray-300">
                    SWP Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/mutul-fund-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Mutual Fund Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/sukanya-samriddhi-yojana-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Samriddhi Yojana Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/fd-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    FD Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/rd-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    RD Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/nps-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    NPS Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/hra-exemption-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    HRA Exemption Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/retirement-planning-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Retirement Planning Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/emi-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    EMI Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/simple-interest-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Simple Interest Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/compound-interest-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Compound Interest Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/nsc-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    NSC Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/step-up-sip-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Step Up Sip Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/income-tax-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Income Tax Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/home-loan-emi-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Home Loan Emi Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/car-loan-emi-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Car Loan Emi Calculator
                  </li>
                </Link>
                {/* {categories.map(category => (
       <Link key={category.id} to={`/category/${category.id}`}>
         <li className="py-2 border-b border-gray-300">{category.name}</li>
       </Link>
     ))} */}
              </ul>
            </div>
          </div>
          <div className="bg-white shadow-md rounded-lg overflow-hidden mt-4">
            <div className="bg-blue-100 px-4 py-2 border-b border-blue-600">
              <h2 className="text-lg font-semibold">More Categories</h2>
            </div>
            <div className="p-2">
              <ul>
                <Link to="/financial-tools-calculators/gratuity-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Gratuity Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/APY-calculator">
                  <li className="py-2 border- hover:bg-slate-100 border-gray-300">
                    APY Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/cagr-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    CAGR Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/gst-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    GST Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/flat-vs-reducing-rate-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Flat Vs Reducing Rate Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/equity-margin-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Equity Margin Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/inflation-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Inflation Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/salary-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Salary Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/brokerage-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Brokerage Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/tds-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    TDS Calculator
                  </li>
                </Link>
                {/* {moreCategories.map(category => (
          <Link key={category.id} to={`/category/${category.id}`}>
            <li className="py-2 border-b border-gray-300">{category.name}</li>
          </Link>
        ))} */}
              </ul>
            </div>
          </div>
        </div>
      </div>
      {/* FAQ section */}
      <div className="max-w-4xl mx-auto py-6 mt-10 px-2">
        <h1 className="text-center text-2xl md:text-2xl lg:text-3xl font-bold mb-8">
          Frequently Asked Questions
        </h1>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default CanaraBankCalculator;
